import Color from '@arcgis/core/Color';
import Point from '@arcgis/core/geometry/Point';
import Graphic from '@arcgis/core/Graphic';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import PictureMarkerSymbol from '@arcgis/core/symbols/PictureMarkerSymbol';
import SimpleLineSymbol from '@arcgis/core/symbols/SimpleLineSymbol';
import SimpleMarkerSymbol from '@arcgis/core/symbols/SimpleMarkerSymbol';
import TextSymbol from '@arcgis/core/symbols/TextSymbol';
import MapView from '@arcgis/core/views/MapView';
import { DEFAULT_MAP_SOURCE } from '@env';
import * as turf from '@turf/turf';
import { Feature } from '@turf/turf';
import { fromEvent, Observable, Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

import { Optional } from '@angular/core';

import { SnMapClusterPoint } from '@lib/api';
import { FUSUS_GREEN } from '@lib/common/constants';
import {
  colorIsDark,
  colorToEsriColorWithOpacity,
  colorToRgbWithOpacity,
  convertOpacityToHex,
} from '@lib/common/helpers';
import { GoogleMap, IAdvancedBadgeOptions } from '@lib/geo/google/map';
import {
  convertNamedIconToUnicode,
  FontAwesomeFontSet,
  fontAwesomeSvgUrl,
  Maps,
  SnLonLat,
  SnLonLatBounds,
} from '@lib/geo/helpers';
import { hasIconProperty, IMapMarkerShape, MapMarkerIcon } from '@lib/geo/map/core';
import { SnBadgeConfig } from '@lib/ui/badge';
import { COLORS } from '@lib/ui/theme';
import fususIcons from 'libs/ui/icon/src/lib/components/fusus-icons/service/icons.json';

import { FsMap, FsMapType, MapType } from '../../types/map';
import { FsMarkerSymbolType } from '../../types/marker';
import { FsMapPopup } from '../popup';
import { BaseMarker } from './base-marker';
import { IMapMarkerIconLabel, IMapMarkerOptions, IMapMarkerStyleOptions } from './marker-options';

import AdvancedMarkerElement = google.maps.marker.AdvancedMarkerElement;
import AdvancedMarkerElementOptions = google.maps.marker.AdvancedMarkerElementOptions;

const DEFAULT_OPTIONS: IMapMarkerOptions = {
  type: FsMarkerSymbolType.PIN,
};

export class FsMapMarker extends BaseMarker {
  public color: string;
  public content: HTMLElement = document.createElement('div');
  public draggable: boolean = false;
  public clickable: boolean = true;
  public isClickHappening: boolean = true;
  public icon: MapMarkerIcon;
  public iconLabel: IMapMarkerIconLabel;
  public rotation: number;
  public size: string;
  public _marker: google.maps.marker.AdvancedMarkerElement | __esri.Graphic[];
  public styles: IMapMarkerStyleOptions;
  public _options: IMapMarkerOptions;
  public mapView: MapView;
  public popup: FsMapPopup<FsMap>;
  public backgroundCircle: Graphic;
  public backgroundLayer: GraphicsLayer;
  public iconsLayer: GraphicsLayer;
  public statusBadge: HTMLElement | Graphic;
  public feature: Feature<Point>;

  protected type: FsMarkerSymbolType;
  protected badge: any;

  private _geometry: Point;
  private readonly _newClick$: Subject<any> = new Subject<any>();
  private readonly _newRightClick$: Subject<any> = new Subject<any>();
  private readonly _newMouseEnter$: Subject<any> = new Subject<any>();
  private readonly _newMouseLeave$: Subject<any> = new Subject<any>();
  private readonly _newDrag$: Subject<any> = new Subject<any>();

  get newClick$(): Observable<any> {
    return this._newClick$.asObservable();
  }

  get newRightClick$(): Observable<any> {
    return this._newRightClick$.asObservable();
  }

  get newMouseEnter$(): Observable<any> {
    return this._newMouseEnter$.asObservable();
  }
  get newMouseLeave$(): Observable<any> {
    return this._newMouseLeave$.asObservable();
  }

  get newDrag$(): Observable<any> {
    return this._newDrag$.asObservable();
  }

  get defaultMapSource(): string {
    return DEFAULT_MAP_SOURCE;
  }

  constructor(
    protected map: FsMap,
    @Optional() options?: IMapMarkerOptions,
    @Optional() public cluster?: SnMapClusterPoint,
  ) {
    super({
      lonLat: options?.lonLat,
      title: options?.title,
      id: options?.id,
    });
    options = { ...DEFAULT_OPTIONS, ...options };

    this._options = {
      size: options.size || '24px',
      zIndex: 100,
      color: typeof options.icon !== 'string' && options.icon?.color ? options.icon?.color : '#fff',
      rotation: options.rotation || 0,
      ...options,
    };
    this.styles = options.style;
    this.id = options.id;
    this.icon = this._options.icon;
    this.size = this._options.size;
    this.color = this._options.color;
    this.rotation = this._options.rotation;
    this.draggable = this._options.draggable;
    this.clickable = this._options.clickable;
    this.type = this._options.type;
    this.iconLabel = this._options.iconLabel;

    if (this.defaultMapSource === Maps.GOOGLE) {
      this._handleGoogleMarker(this.map.mapInstance as unknown as MapType<'google'>, options);
    } else {
      this.mapView = (this.map as unknown as FsMapType<'esri'>).mapView;
      this._initializeGraphicsLayers();
      this._handleEsriMarker(options);

      if (this._options.attributes) {
        (this._marker as unknown as Graphic[]).forEach((marker) => {
          marker.attributes = options.attributes;
        });
      }

      this._setupEsriHoverHandler();
      if (this.styles) {
        this.setStyle(this.styles);
      }
      if (this._options.clickable) {
        this._setupEsriClickHandler();
      }
      if (this._options.draggable) {
        this._setupEsriDragHandler();
      }
    }
    if (cluster) {
      this._setupClusterClickHandler();
    }

    if (this.iconLabel?.text) {
      const internalId = this._options.attributes.properties?.users?.[0]?.internalId;
      const iconLabel = document.createElement('span');
      iconLabel.innerText = internalId ? internalId : this.iconLabel.text;
      this.content.appendChild(iconLabel);
      iconLabel.classList.add('fs__advanced-feature-marker-label');
      iconLabel.style.top = `${this.iconLabel.top}px`;
      iconLabel.style.left = `${this.iconLabel.left}px`;
    }
    this.content = this._options.content || this.content;
  }

  public getId(): string {
    return this.id;
  }

  public getAttributes(): any {
    return this._options.attributes;
  }

  public getMarker(): google.maps.marker.AdvancedMarkerElement | __esri.Graphic[] {
    return this._marker;
  }

  public getPaths(iconName: string): any[] {
    return fususIcons.icons.find((icon) => icon.properties.name === iconName)?.icon.paths;
  }

  public getCenter(): {
    center: number[];
  } {
    return {
      center: [this.getLonLat().lat, this.getLonLat().lon],
    };
  }

  public updateMarkerPosition(newPosition: { lat: number; lng: number }): this {
    if (this._marker instanceof google.maps.marker.AdvancedMarkerElement) {
      this._marker.position = new google.maps.LatLng(newPosition.lat, newPosition.lng);
    } else {
      this._marker.forEach((graphic) => {
        graphic.geometry = new Point({
          latitude: newPosition.lat,
          longitude: newPosition.lng,
        });
        this._geometry = new Point({
          latitude: newPosition.lat,
          longitude: newPosition.lng,
        });
      });
    }

    return this;
  }

  public setIcon(icon: MapMarkerIcon): this {
    this.icon = typeof icon === 'string' ? { name: icon, color: '#000' } : icon;

    this._updateIcon();

    return this;
  }

  public getBadge(): SnBadgeConfig {
    return this.badge;
  }

  public setBadge(badge: SnBadgeConfig): this {
    this.badge = badge;
    this._updateBadge();

    return this;
  }

  public setPopup(popup: FsMapPopup<FsMap>): void {
    this.popup = popup;
  }

  public setMap(map: FsMap): this {
    this.map = map;

    return this;
  }

  public getGeometry(): any {
    const coordinates = [this.getLonLat().lon, this.getLonLat().lat];
    return turf.geometry('Point', coordinates);
  }

  public setBadgeStatus(status: string, badgePosition: IAdvancedBadgeOptions): void {
    if (this.defaultMapSource === Maps.GOOGLE) {
      if (this.statusBadge) {
        (this.statusBadge as unknown as HTMLElement).style.backgroundColor = status;
        Object.entries(badgePosition).forEach(([position, offset]) => {
          (this.statusBadge as unknown as HTMLElement).style[position] = offset;
        });
      } else {
        const badge = document.createElement('span');
        badge.classList.add('fs__advanced-marker-status-badge');
        badge.style.backgroundColor = status;
        Object.entries(badgePosition).forEach(([position, offset]) => {
          badge.style[position] = offset;
        });
        this.content.appendChild(badge);
        this.statusBadge = badge;
      }
    } else {
      if (this.statusBadge) {
        (this.statusBadge as unknown as Graphic).symbol.color = status as unknown as Color;
      } else {
        const badgeBackgroundSymbol = new SimpleMarkerSymbol({
          style: 'circle',
          color: status,
          size: 8,
          outline: {
            color: '#000',
            width: 0.5,
          },
          xoffset: 11,
          yoffset: -11,
        });

        const badgeBackgroundGraphic = new Graphic({
          geometry: this._geometry,
          symbol: badgeBackgroundSymbol,
        });

        this.statusBadge = badgeBackgroundGraphic;
        this.mapView.graphics.add(badgeBackgroundGraphic);
      }
    }
  }

  public createBadge(badgeClass?: string): void {
    if (this.defaultMapSource === Maps.GOOGLE) {
      const badge = document.createElement('span');
      badge.innerText = this.cluster.properties.objects?.toString();
      badge.classList.add('fs__cluster-badge');
      badge.classList.add(badgeClass || 'fs__map-marker-cluster-badge');
      (this.getMarker() as AdvancedMarkerElement).content.appendChild(badge);
    } else {
      const badgeBackgroundSymbol = new SimpleMarkerSymbol({
        style: 'circle',
        color: 'rgb(255, 255, 255, 0.7)',
        size: this._calculateBadgeSize(this.cluster.properties.objects?.toString()),
        outline: {
          color: [255, 255, 255],
          width: 2,
        },
        xoffset: 12,
        yoffset: 12,
      });

      const badgeSymbol = new TextSymbol({
        text: this.cluster.properties.objects?.toString(),
        color: '#000',
        font: {
          size: 7,
          weight: 'lighter',
        },
        horizontalAlignment: 'center',
        verticalAlignment: 'middle',
        xoffset: 12,
        yoffset: 12,
      });

      const badgeBackgroundGraphic = new Graphic({
        geometry: this._geometry,
        symbol: badgeBackgroundSymbol,
      });

      const badgeGraphic = new Graphic({
        geometry: this._geometry,
        symbol: badgeSymbol,
      });

      this.badge = [badgeBackgroundGraphic, badgeGraphic];
      this.mapView.graphics.addMany([badgeBackgroundGraphic, badgeGraphic]);
    }
  }

  public createSelectedBadge(): void {
    if (this.defaultMapSource === Maps.GOOGLE) {
      this.badge = document.createElement('span');
      this.badge.innerText = '✓';
      this.badge.classList.add('fs__map-marker-selected-badge');
      this.badge.id = 'selected-badge';
      (this.getMarker() as AdvancedMarkerElement).content.appendChild(this.badge);
    } else {
      const badgeBackgroundSymbol = new SimpleMarkerSymbol({
        style: 'circle',
        color: FUSUS_GREEN,
        outline: {
          color: FUSUS_GREEN,
        },
        size: 12,
        xoffset: 12,
        yoffset: 8,
      });

      const badgeSymbol = new TextSymbol({
        text: '\u2714', // unicode checkmark
        color: '#fff',
        font: {
          size: 7,
          weight: 'lighter',
        },
        horizontalAlignment: 'center',
        verticalAlignment: 'middle',
        xoffset: 12,
        yoffset: 8,
      });

      const badgeBackgroundGraphic = new Graphic({
        geometry: this._geometry,
        symbol: badgeBackgroundSymbol,
      });

      const badgeGraphic = new Graphic({
        geometry: this._geometry,
        symbol: badgeSymbol,
      });

      this.badge = [badgeBackgroundGraphic, badgeGraphic];
      this.mapView.graphics.addMany([badgeBackgroundGraphic, badgeGraphic]);
    }
  }

  public removeSelectedBadge(): void {
    if (this.badge) {
      if (this.defaultMapSource === Maps.GOOGLE) {
        (this.getMarker() as AdvancedMarkerElement).content.removeChild(this.badge);
      } else {
        this.mapView.graphics.removeMany(this.badge);
      }
    }
  }

  public removeMarker(): void {
    if (this.defaultMapSource === Maps.GOOGLE) {
      (this.map as unknown as GoogleMap).removeMarker(this._marker as AdvancedMarkerElement);
    } else {
      this.mapView.graphics.removeMany(this._marker as Graphic[]);
      this.mapView.graphics.removeMany(this.badge as Graphic[]);
      this.backgroundLayer.graphics.remove(this.backgroundCircle);
      this.iconsLayer.graphics.removeMany(this._marker as Graphic[]);
    }
  }

  public setStyle(style: IMapMarkerStyleOptions): this {
    this.styles = {
      ...this.styles,
      ...style,
    };
    if (this.defaultMapSource === Maps.GOOGLE) {
      this.setStyles(this.content);
    } else {
      this.setEsriStyles();
    }
    return this;
  }

  public setDraggable(draggable: boolean): this {
    this.draggable = draggable;
    switch (this.defaultMapSource) {
      case Maps.GOOGLE:
        (this._marker as google.maps.marker.AdvancedMarkerElement).gmpDraggable = this.draggable;
        break;
      case Maps.ESRI:
        if (draggable) this._setupEsriDragHandler();
    }

    return this;
  }

  public setRotation(rotation: number): void {
    let tempMarker;
    switch (this.defaultMapSource) {
      case 'google':
        const content: any = (this.getMarker() as AdvancedMarkerElement).content;
        const img = content.querySelector('.fs__camera-advanced-icon');
        img
          ? (img.style.transform = `rotate(${rotation}deg)`)
          : (content.style.transform = `rotate(${rotation}deg)`);
        break;
      case 'esri':
        (this._marker as Graphic[]).forEach((graphic) => {
          this.iconsLayer.remove(graphic);
          this.mapView.graphics.remove(graphic);
          tempMarker = graphic.clone();
          tempMarker.symbol.set('angle', rotation);
          this.mapView.graphics.add(tempMarker);
          this._marker = [tempMarker];
          tempMarker = null;
        });
        break;
    }
  }

  public setStyles(content: HTMLElement): void {
    const borderColor = this.styles.strokeColor || '#fff';
    const borderWeight = this.styles.strokeWeight || 0;
    const colorBasedOnFillColor = colorIsDark(this.styles.fillColor) ? COLORS['grey-0'] : '#000000';
    content.style.background = this.styles.fillColor
      ? `${this.styles.fillColor}${convertOpacityToHex(this.styles.fillOpacity)}`
      : COLORS['base-800'];
    content.style.color = this.styles.overrideColor ? this.color : colorBasedOnFillColor;
    content.style.width = `${this.styles.scale ? this.styles.scale * 32 : 32}px`;
    content.style.height = `${this.styles.scale ? this.styles.scale * 32 : 32}px`;
    content.style.borderRadius = '50%';
    if (this.styles.border) {
      content.style.border = this.styles.border;
    } else {
      content.style.border = borderWeight ? `${borderWeight}px solid ${borderColor}` : 'none';
    }
  }

  public setEsriStyles(): void {
    if (this.styles.fillOpacity === 0 && this.styles.strokeOpacity === 0) {
      this.backgroundLayer.remove(this.backgroundCircle);
      return;
    } else {
      this.backgroundCircle.visible = this.styles.fillOpacity > 0 || this.styles.strokeOpacity > 0;
      this.backgroundCircle.symbol.color = colorToEsriColorWithOpacity(
        this.styles.fillColor || COLORS['base-800'],
        this.styles?.fillOpacity || 1,
      );
      (this.backgroundCircle.symbol as SimpleMarkerSymbol).outline = new SimpleLineSymbol({
        color: colorToEsriColorWithOpacity(
          this.styles.strokeColor || '#fff',
          this.styles?.strokeOpacity || 1,
        ),
        width: this.styles?.strokeWeight || 0,
      });
    }

    this.backgroundLayer.remove(this.backgroundCircle);
    this.backgroundLayer.add(this.backgroundCircle);
  }

  private _calculateBadgeSize(text: string): number {
    return 4 + text.length * 4;
  }

  private _handleGoogleMarker(
    map: MapType<'google'>,
    marker: any,
  ): google.maps.marker.AdvancedMarkerElement {
    if (!marker.lonLat) {
      return;
    }
    if (!this.icon && this.type === FsMarkerSymbolType.PIN) {
      this._createDefaultGooglePinMarker(map);
      return;
    }

    const markerLonLat = SnLonLat.convert(marker.lonLat).toArray();

    const markerOptions: AdvancedMarkerElementOptions = {
      content: this.content || new google.maps.marker.PinElement({ scale: 1 }),
      position: new google.maps.LatLng(markerLonLat[1], markerLonLat[0]),
      title: this.title,
      map,
      gmpDraggable: this.draggable,
      gmpClickable: this.clickable,
      zIndex: 1000,
      collisionBehavior: marker.collisionBehavior,
    };

    if (this.icon) {
      if (hasIconProperty(this.icon, 'name')) {
        this.content.innerHTML = `
        <i class="material-icons">${this.icon.name}</i>
      `;
        this.content.classList.add('fs__center-marker');
        this.content.classList.add('fs__advanced-marker');
        this.content.classList.add('fs__advanced-feature-image-marker');
        markerOptions.content = this.content;
      }
      if (hasIconProperty(this.icon, 'url')) {
        this.content.innerHTML = `
        <img src="${this.icon.url}" alt="Marker Icon"/>
      `;
        this.content.classList.add('fs__center-marker');
        this.content.classList.add('fs__advanced-marker');
        this.content.classList.add('fs__advanced-feature-image-marker');
        markerOptions.content = this.content;
      }
      if (hasIconProperty(this.icon, 'fontSet') && hasIconProperty(this.icon, 'fontIcon')) {
        const transform = [`scale(${this.styles?.scale || 1})`];
        if (this.icon.fontSet === 'fs') {
          const fontIcon = this.icon.fontIcon?.toString() || '';
          const paths = this.getPaths(fontIcon);
          const icon = document.createElement('span');
          icon.classList.value = `fs ${this.icon.fontIcon}`;
          icon.style.fontSize =
            (this.styles?.scale ? `${this.styles.scale * parseInt(this.size, 10)}px` : this.size) ||
            '24px';
          icon.style.transform = transform.join(' ');
          if (paths) {
            icon.style.display = 'flex';
            icon.style.justifyContent = 'center';
            icon.style.alignItems = 'center';
            paths.forEach((el, i) => {
              const t = document.createElement('span');
              t.classList.value = `path${i + 1}`;
              icon?.appendChild(t);
            });
          }
          this.content.classList.add('fs__advanced-feature-marker');
          this.content.classList.add('fs__center-marker');
          this.content.classList.add('fs__advanced-marker');
          icon.classList.add('fs__advanced-feature-icon');
          this.content.appendChild(icon);
          markerOptions.content = this.content;
        } else {
          const markerDiv = document.createElement('div');
          markerDiv.className = 'fs__marker';
          const markerSize = (parseInt(this.size, 10) * 5) / 3;
          const sizeValue =
            (this.styles?.scale ? `${this.styles.scale * markerSize}px` : `${markerSize}px`) ||
            '40px';
          markerDiv.style.width = sizeValue;
          markerDiv.style.height = sizeValue;
          if (this.styles?.fillColor) {
            markerDiv.style.backgroundColor = this.styles?.fillColor;
          }

          const icon = document.createElement('i');
          icon.classList.value = `${this._toFaSet(this.icon.fontSet as string)} fa-${
            this.icon.fontIcon
          }`;
          icon.style.fontSize =
            (this.styles?.scale ? `${this.styles.scale * parseInt(this.size, 10)}px` : this.size) ||
            '24px';

          if (this.styles?.rotation) transform.push(`rotate(${this.styles.rotation}deg)`);
          icon.style.transform = transform.join(' ');

          if (this.color) {
            icon.style.color = this.color;
          }

          icon.classList.add('fs__map-marker-icon');

          if (this.styles?.shape === IMapMarkerShape.Circle) {
            markerDiv.appendChild(icon);
          } else {
            const pinDiv = document.createElement('div');
            pinDiv.className = 'fs__marker-pin';
            if (this.styles?.fillColor || this.styles?.fillOpacity) {
              pinDiv.style.backgroundColor = this.styles.fillColor;
              pinDiv.style.opacity = `${
                this.styles?.fillOpacity ? this.styles.fillOpacity * 100 : 100
              }%`;
            }
            if (this.styles?.strokeColor) {
              pinDiv.style.border = `2px solid ${this.styles.strokeColor}`;
            }

            pinDiv.appendChild(icon);
            markerDiv.appendChild(pinDiv);
          }
          this.content = markerDiv;
          this.content.classList.add('fs__advanced-feature-marker');
          this.content.classList.add('fs__advanced-marker');
          markerOptions.content = this.content;
        }
      }
      if (hasIconProperty(this.icon, 'text')) {
        this.content.innerHTML = `${this.icon.text}`;
        this.content.classList.add('fs__center-marker');
        this.content.classList.add('fs__advanced-marker');
        this.content.classList.add('fs__advanced-feature-image-marker');
        markerOptions.content = this.content;

        if (this._options.textSymbolOptions?.isLabel) {
          this.content.classList.add('fs__advanced-icon-marker-label');
        }
      }
    }

    if (this.styles) {
      this.setStyles(this.content);
    }

    const newMarker = new google.maps.marker.AdvancedMarkerElement({
      ...markerOptions,
      zIndex: 1000,
    });

    if (this._options.clickable) {
      const clicks = fromEvent(document, 'click');
      const result = clicks.pipe(debounceTime(1000));
      newMarker.addListener('click', (e) => {
        if (this.isClickHappening) {
          this.isClickHappening = false;
          this._onClick(marker, e);
          result.subscribe(() => {
            this.isClickHappening = true;
          });
        }
      });
    }
    if (this._options.clickable) {
      newMarker.content.addEventListener('contextmenu', (e) => this._onClick(marker, e));
    }
    newMarker.content.addEventListener('mouseenter', () => this._onMouseEnter(marker));
    newMarker.content.addEventListener('mouseleave', () => this._onMouseLeave(marker));
    newMarker.addListener('dragend', (e) => this._onDragEnd(e));
    this._marker = newMarker;
    return newMarker;
  }

  private _initializeGraphicsLayers(): void {
    this.backgroundLayer = this.mapView.map.layers.find(
      (layer: any) => layer instanceof GraphicsLayer && layer.id === 'backgroundLayer',
    ) as GraphicsLayer;

    if (!this.backgroundLayer) {
      this.backgroundLayer = new GraphicsLayer({ id: 'backgroundLayer' });
      this.mapView.map.add(this.backgroundLayer, 1);
    }

    this.iconsLayer = this.mapView.map.layers.find(
      (layer: any) => layer instanceof GraphicsLayer && layer.id === 'iconsLayer',
    ) as GraphicsLayer;

    if (!this.iconsLayer) {
      this.iconsLayer = new GraphicsLayer({ id: 'iconsLayer' });
      this.mapView.map.add(this.iconsLayer, 2);
    }
  }

  private _handleEsriMarker(marker: IMapMarkerOptions): __esri.Graphic[] {
    if (!marker.lonLat) {
      return;
    }
    const markerLonLat = SnLonLat.convert(marker.lonLat).toArray();
    const geometry = new Point({
      latitude: markerLonLat[1],
      longitude: markerLonLat[0],
    });

    this._geometry = geometry;
    const graphic = new Graphic({
      geometry,
    });

    const circleSymbol = new SimpleMarkerSymbol({
      style: 'circle',
      color: colorToRgbWithOpacity(
        this.styles?.fillColor || '#ff0000',
        this.styles?.fillOpacity || 1,
      ),
      size: this.styles?.scale ? this.styles.scale * 24 : 24,
      outline: {
        color: this.styles?.strokeColor || '#fff',
        width: this.styles?.strokeWeight || 0,
      },
    });

    this.backgroundCircle = new Graphic({
      geometry: this._geometry,
      symbol: circleSymbol,
      visible: false,
    });

    if (hasIconProperty(this.icon, 'fontSet') && hasIconProperty(this.icon, 'fontIcon')) {
      if (this.icon.fontSet === 'fs') {
        const fontIcon = this.icon.fontIcon?.toString() || '';
        const symbol = new PictureMarkerSymbol({
          url: `/assets/fusus-icons/SVG/${fontIcon}.svg`,
          color: this.color,
          angle: this.styles?.rotation || 0,
          width: this.styles?.scale ? this.styles.scale * 16 : 16,
          height: this.styles?.scale ? this.styles.scale * 16 : 16,
        });

        const markerGraphic = new Graphic({
          geometry,
          symbol,
        });

        this.backgroundLayer.add(this.backgroundCircle);
        this.iconsLayer.add(markerGraphic);
        this._marker = [markerGraphic];
        return [markerGraphic];
      }
      return this._createEsriFontAwesomeMarker({
        fontSet: this.icon.fontSet as unknown as FontAwesomeFontSet,
        fontIcon: this.icon.fontIcon as unknown as string,
        coordinates: [markerLonLat[0], markerLonLat[1]],
      });
    } else if (hasIconProperty(this.icon, 'name')) {
      const symbol = new TextSymbol({
        color: this.color,
        text: convertNamedIconToUnicode(this.icon.name as string),
        font: {
          size: 12,
          family: 'CalciteWebCoreIcons',
        },
        horizontalAlignment: 'center',
        verticalAlignment: 'middle',
      });
      const markerGraphic = new Graphic({
        geometry,
        symbol,
      });

      this.iconsLayer.add(markerGraphic);
      this._marker = [markerGraphic];
      return [markerGraphic];
    } else if (hasIconProperty(this.icon, 'url')) {
      const symbol = new PictureMarkerSymbol({
        url: this.icon.url as string,
      });
      graphic.set({ symbol });
    } else if (hasIconProperty(this.icon, 'text')) {
      const symbol = new TextSymbol({
        text: this.icon.text,
        color: '#fff',
        haloSize: 1,
        haloColor: '#000',
        font: {
          size: 16,
          weight: 'lighter',
        },
        horizontalAlignment: 'center',
        verticalAlignment: 'middle',
        ...this._options.textSymbolOptions,
      });
      graphic.set({ symbol });
    } else {
      const symbol = new SimpleMarkerSymbol({
        color: this.color,
      });
      graphic.set({ symbol });
    }

    this.iconsLayer.add(graphic);
    this._marker = [graphic];
    return [graphic];
  }

  private _setupClusterClickHandler(): void {
    const clusterMarker = this.getMarker();

    if (this.defaultMapSource === Maps.GOOGLE) {
      (clusterMarker as AdvancedMarkerElement).addListener('click', () => {
        if (this.cluster.bbox) {
          const lonLatBounds = SnLonLatBounds.fromGeojsonBBox(this.cluster.bbox);
          const bounds = SnLonLatBounds.convertToLatLng(lonLatBounds);
          (this.map as unknown as GoogleMap).panToBounds(lonLatBounds);
          this.map.setCenter(this.cluster.geometry.coordinates);
          (this.map as unknown as GoogleMap).mapInstance.fitBounds(bounds);
        }
      });
    } else {
      if (this.cluster.bbox) {
        this.mapView.on('click', (event) => {
          this.mapView.hitTest(event).then((response: any) => {
            if (response.results.length > 0) {
              const graphicResult = response.results.find((result) =>
                (this._marker as Graphic[]).includes(result.graphic),
              );
              if (graphicResult) {
                const lonLatBounds = SnLonLatBounds.fromGeojsonBBox(this.cluster.bbox);
                const extent = SnLonLatBounds.convertToExtent(lonLatBounds);
                this.mapView.goTo({
                  target: extent,
                });
              }
            }
          });
        });
      }
    }
  }

  private _setupEsriHoverHandler(): void {
    let isHovering = false;
    let hoverTimeout: number | null = null;
    let leaveTimeout: number | null = null;

    this.mapView.on('pointer-move', (event) => {
      if (hoverTimeout) {
        clearTimeout(hoverTimeout);
      }

      hoverTimeout = window.setTimeout(() => {
        this.mapView.hitTest(event).then((response: any) => {
          if (response.results.length > 0) {
            const graphicResult = response.results.find((result) =>
              (this._marker as Graphic[]).includes(result.graphic),
            );
            if (graphicResult) {
              if (!isHovering) {
                isHovering = true;
                if (this.popup) {
                  this.popup.open();
                }
                if (leaveTimeout) {
                  clearTimeout(leaveTimeout);
                  leaveTimeout = null;
                }
                this._onMouseEnter(graphicResult.graphic);
              } else if (isHovering) {
                if (!leaveTimeout) {
                  leaveTimeout = window.setTimeout(() => {
                    isHovering = false;
                    if (this.popup) {
                      this.popup.close();
                    }
                    this._onMouseLeave(graphicResult.graphic);
                  }, 25); // Adjust the delay as needed
                }
              }
            }
          }
        });
      }, 50);
    });
  }

  private _setupEsriClickHandler(): void {
    this.mapView.on('click', (event) => {
      this.mapView.hitTest(event).then((response: any) => {
        if (response.results.length > 0) {
          const graphicResult = response.results.find((result) =>
            (this._marker as Graphic[]).includes(result.graphic),
          );
          if (graphicResult) {
            this._onClick(
              {
                graphic: graphicResult.graphic,
                geometry: graphicResult.graphic.geometry,
                ...this._options,
              },
              event,
            );
          }
        }
      });
    });
  }

  private _setupEsriDragHandler(): void {
    let draggingGraphic;
    let tempGraphic;
    let newGraphic;

    this.mapView.on('drag', (event) => {
      if (event.action === 'start') {
        if (newGraphic) {
          this.iconsLayer.remove(newGraphic);
        }
        this.mapView.hitTest(event).then((response: any) => {
          if (response.results.length > 0) {
            draggingGraphic = response.results.find((result) => {
              return (this._marker as Graphic[]).includes(result.graphic);
            });
            this.iconsLayer.remove(draggingGraphic);
          }
        });
      } else if (event.action === 'update') {
        if (draggingGraphic) {
          event.stopPropagation();

          if (tempGraphic) {
            this.mapView.graphics.remove(tempGraphic);
          } else {
            this.mapView.graphics.remove(draggingGraphic.graphic);
          }
          tempGraphic = draggingGraphic.graphic.clone();
          tempGraphic.geometry = this.mapView.toMap(event);
          this.mapView.graphics.add(tempGraphic);
        }
      } else if (event.action === 'end') {
        if (draggingGraphic) {
          event.stopPropagation();
          if (tempGraphic) {
            this.mapView.graphics.remove(tempGraphic);
          }
          newGraphic = draggingGraphic.graphic.clone();
          newGraphic.geometry = tempGraphic.geometry.clone();
          this._geometry = newGraphic.geometry;
          this.mapView.graphics.add(newGraphic);
          this.iconsLayer.add(newGraphic);
          this._marker = [newGraphic];
          this._onDragEnd(this._marker[0]);

          draggingGraphic = null;
          tempGraphic = null;
        }
      }
    });
  }

  private _onClick(object: any, event: any): void {
    if (this.defaultMapSource === Maps.GOOGLE) {
      this._newClick$.next(object);
    } else {
      if (
        event.button === 0 ||
        event.srcButton?.button === 0 ||
        event.srcEvent?.domEvent?.button === 0
      ) {
        this._newClick$.next(object);
      } else if (
        event.button === 2 ||
        event.srcButton?.button === 2 ||
        event.srcEvent?.domEvent?.button === 2
      ) {
        this._newRightClick$.next(object);
      }
    }
  }

  private _onMouseEnter(object: any): void {
    this._newMouseEnter$.next(object);
  }

  private _onMouseLeave(object: any) {
    this._newMouseLeave$.next(object);
  }

  private _onDragEnd(object: any): void {
    const latLng = this._getLatLng(object);
    const data = {
      marker: object,
      latLng,
    };
    this._newDrag$.next(data);
  }

  private _createEsriFontAwesomeMarker(options: {
    fontSet: FontAwesomeFontSet;
    fontIcon: string;
    coordinates: [number, number];
  }): Graphic[] {
    const { fontSet, fontIcon, coordinates } = options;

    const iconSymbol = new PictureMarkerSymbol({
      url: fontAwesomeSvgUrl(fontSet, fontIcon),
      angle: this.styles?.rotation || 0,
      width: this.styles?.scale ? this.styles.scale * 11 : 11,
      height: this.styles?.scale ? this.styles.scale * 11 : 11,
      yoffset: 2,
    });

    const markerSymbol = {
      type: 'simple-marker',
      path: 'M 12 2 C 8.134 2 5 5.134 5 9 c 0 5.25 7 13 7 13 s 7 -7.75 7 -13 c 0 -3.866 -3.134 -7 -7 -7 z z',
      color: '#fff', // Green color
      outline: {
        color: '#fff', // Border color
        width: 1,
      },
      size: 24,
      angle: 0,
    };

    const backgroundLayer = new Graphic({
      geometry: this._geometry,
      symbol: markerSymbol,
    });

    const point = new Point({
      longitude: coordinates[0],
      latitude: coordinates[1],
    });

    const pointGraphic = new Graphic({
      geometry: point,
      symbol: iconSymbol,
    });

    this.backgroundCircle = backgroundLayer;
    this.backgroundLayer.add(backgroundLayer);
    this.iconsLayer.add(pointGraphic);
    this._marker = [pointGraphic];
    return [backgroundLayer, pointGraphic];
  }

  private _toFaSet(fontSet: string): string {
    switch (fontSet) {
      case 'fat':
        return 'fa-thin';
      case 'fal':
        return 'fa-light';
      case 'far':
        return 'fa-regular';
      case 'fas':
        return 'fa-solid';
      case 'fab':
        return 'fa-brands';
      case 'fad':
        return 'fa-duotone';
    }
  }

  private _updateIcon(): void {
    return;
  }

  private _getLatLng(marker: google.maps.marker.AdvancedMarkerElement | Graphic): any {
    let latLng;
    if (this.defaultMapSource === Maps.GOOGLE) {
      latLng = {
        lat: (marker as any).latLng?.lat(),
        lng: (marker as any).latLng?.lng(),
      };
    } else {
      latLng = {
        lat: (marker as any).geometry.latitude,
        lng: (marker as any).geometry.longitude,
      };
    }
    return latLng;
  }
  private _updateBadge(): void {
    return;
  }

  private _createDefaultGooglePinMarker(map: MapType<'google'>) {
    const pinElement = new google.maps.marker.PinElement({
      scale: 0.8,
      glyph: '',
    });

    const defaultPinMarker = new google.maps.marker.AdvancedMarkerElement({
      map,
      position: this.lonLat.toLatLngLiteral(),
      title: this.title,
      content: pinElement.element,
    });
  }
}
